import { useState, useEffect } from "react";

/**
 * ScenarioAnimation — small SVG scenes per `animation_key`.
 *
 * Lightweight: a single SVG per family (roundabouts, junctions, parking),
 * with an animated dot following a `<path>`. No third-party assets — every
 * shape is drawn inline. The `play` toggle resets the animation by remounting
 * the path so the user can rewatch deliberately.
 */
export default function ScenarioAnimation({ animationKey }) {
  const [tick, setTick] = useState(0);
  useEffect(() => { setTick((t) => t + 1); }, [animationKey]);
  const replay = () => setTick((t) => t + 1);

  const Body = SCENES[animationKey] || SCENES.fallback;

  return (
    <div data-testid="scenario-animation" className="rounded-2xl bg-slate-900 border border-slate-700 p-3 relative">
      <Body key={tick} />
      <button
        data-testid="scenario-replay"
        onClick={replay}
        className="absolute top-3 right-3 text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded-full bg-white/10 text-white hover:bg-white/20"
      >Replay</button>
    </div>
  );
}

const Path = ({ d, dash, stroke = "#94a3b8", width = 2 }) => (
  <path d={d} stroke={stroke} strokeWidth={width} fill="none"
        strokeDasharray={dash} strokeLinecap="round" />
);

const Car = ({ pathD, dur = 4, fill = "#22d3ee" }) => (
  <g>
    <circle r="6" fill={fill}>
      <animateMotion dur={`${dur}s`} repeatCount="indefinite" path={pathD} />
    </circle>
    <circle r="3" fill="white" opacity="0.7">
      <animateMotion dur={`${dur}s`} repeatCount="indefinite" path={pathD} />
    </circle>
  </g>
);

const SCENES = {
  /* ─── ROUNDABOUTS ─── */
  roundabout_mini_right: () => (
    <svg viewBox="0 0 320 200" className="w-full h-44">
      <rect width="320" height="200" fill="#0f172a" />
      <Path d="M0 100 H140 M180 100 H320" />
      <Path d="M160 0 V60 M160 140 V200" />
      <circle cx="160" cy="100" r="24" stroke="#475569" fill="none" strokeWidth="2" />
      <circle cx="160" cy="100" r="6" fill="#fbbf24" opacity=".4" />
      <Path d="M40 90 H140" stroke="#475569" dash="4 4" />
      <Path d="M180 110 H280" stroke="#475569" dash="4 4" />
      <Car pathD="M0,100 L130,100 A30,30 0 1,0 160,70 L160,0" dur="5" />
    </svg>
  ),
  roundabout_single_ahead: () => (
    <svg viewBox="0 0 320 200" className="w-full h-44">
      <rect width="320" height="200" fill="#0f172a" />
      <Path d="M0 110 H110 M210 110 H320" />
      <Path d="M0 90 H110 M210 90 H320" stroke="#475569" dash="6 4" />
      <Path d="M150 0 V60 M150 160 V200 M170 0 V60 M170 160 V200" stroke="#475569" />
      <circle cx="160" cy="100" r="40" stroke="#475569" fill="none" />
      <Car pathD="M0,100 L120,100 A40,40 0 0,0 160,60 A40,40 0 0,0 200,100 L320,100" dur="5" fill="#34d399" />
    </svg>
  ),
  roundabout_two_lane_right: () => (
    <svg viewBox="0 0 320 200" className="w-full h-44">
      <rect width="320" height="200" fill="#0f172a" />
      <Path d="M0 90 H100 M0 110 H100" />
      <Path d="M0 100 H100" stroke="#475569" dash="6 4" />
      <Path d="M220 90 H320 M220 110 H320" />
      <Path d="M150 0 V60 M170 0 V60" />
      <Path d="M150 140 V200 M170 140 V200" />
      <circle cx="160" cy="100" r="44" stroke="#475569" fill="none" />
      <circle cx="160" cy="100" r="28" stroke="#475569" fill="none" strokeDasharray="4 4" />
      <Car pathD="M0,90 L110,90 A30,30 0 1,1 130,130 L160,200" dur="6" fill="#f472b6" />
    </svg>
  ),
  /* ─── JUNCTIONS ─── */
  junction_t_left: () => (
    <svg viewBox="0 0 320 200" className="w-full h-44">
      <rect width="320" height="200" fill="#0f172a" />
      <Path d="M0 80 H320 M0 120 H320" />
      <Path d="M0 100 H320" stroke="#475569" dash="6 4" />
      <Path d="M150 120 V200 M170 120 V200" />
      <Car pathD="M160,200 L160,130 L60,90 L0,90" dur="4" />
    </svg>
  ),
  junction_t_right: () => (
    <svg viewBox="0 0 320 200" className="w-full h-44">
      <rect width="320" height="200" fill="#0f172a" />
      <Path d="M0 80 H320 M0 120 H320" />
      <Path d="M0 100 H320" stroke="#475569" dash="6 4" />
      <Path d="M150 120 V200 M170 120 V200" />
      <Car pathD="M160,200 L160,130 L260,110 L320,110" dur="4" fill="#f59e0b" />
    </svg>
  ),
  junction_crossroads: () => (
    <svg viewBox="0 0 320 200" className="w-full h-44">
      <rect width="320" height="200" fill="#0f172a" />
      <Path d="M0 80 H320 M0 120 H320" />
      <Path d="M150 0 V200 M170 0 V200" />
      <Path d="M0 100 H320" stroke="#475569" dash="6 4" />
      <Path d="M160 0 V200" stroke="#475569" dash="6 4" />
      <Car pathD="M160,0 L160,200" dur="6" />
      <Car pathD="M0,100 L320,100" dur="6" fill="#10b981" />
    </svg>
  ),
  /* ─── PARKING ─── */
  parking_bay_forward: () => (
    <svg viewBox="0 0 320 200" className="w-full h-44">
      <rect width="320" height="200" fill="#0f172a" />
      <Path d="M0 150 H320" stroke="#475569" dash="6 4" />
      {[60, 130, 200].map((x) => (
        <g key={x}>
          <Path d={`M${x} 30 V120 M${x + 50} 30 V120 M${x} 120 H${x + 50}`} stroke="#64748b" />
        </g>
      ))}
      <Car pathD="M0,150 L100,150 L155,80" dur="5" fill="#22d3ee" />
    </svg>
  ),
  parking_bay_reverse: () => (
    <svg viewBox="0 0 320 200" className="w-full h-44">
      <rect width="320" height="200" fill="#0f172a" />
      <Path d="M0 150 H320" stroke="#475569" dash="6 4" />
      {[60, 130, 200].map((x) => (
        <g key={x}>
          <Path d={`M${x} 30 V120 M${x + 50} 30 V120 M${x} 120 H${x + 50}`} stroke="#64748b" />
        </g>
      ))}
      <Car pathD="M40,150 L240,150 L240,135 L155,90" dur="6" fill="#f472b6" />
    </svg>
  ),
  parking_parallel: () => (
    <svg viewBox="0 0 320 200" className="w-full h-44">
      <rect width="320" height="200" fill="#0f172a" />
      <Path d="M0 130 H320" stroke="#475569" dash="6 4" />
      <rect x="40" y="100" width="80" height="30" fill="none" stroke="#64748b" />
      <rect x="200" y="100" width="80" height="30" fill="none" stroke="#64748b" />
      <Car pathD="M280,80 L160,80 L130,110 L160,115 L160,115" dur="6" fill="#34d399" />
    </svg>
  ),
  fallback: () => (
    <svg viewBox="0 0 320 200" className="w-full h-44">
      <rect width="320" height="200" fill="#0f172a" />
      <text x="160" y="105" textAnchor="middle" fill="#64748b" fontSize="12">No animation yet</text>
    </svg>
  ),
};
